import { useCallback, useMemo } from 'react';

import { IKnmErknmFormViolationEvent } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { ErknmActViolationEventMaterials } from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationEventMaterials';
import { createPassportEventSelectOptions } from 'KnmErknmPackage/ErknmViolationsModule/common/createPassportEventSelectOptions';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';

import styles from './ErknmViolationItem.module.scss';
import fixFactTypeStyles from '../../../DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixFactTypeField.module.scss';

interface IErknmActViolationEventItemProps {
  event: IKnmErknmFormViolationEvent;
  eventIdx: number;
  fieldName: string;
  name: string;
  events: IKnmErknmFormViolationEvent[] | undefined;
  passportEvents: IKnmErknmFormEvent[] | undefined;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  canEdit: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  id: string;
}

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);

export const ErknmActViolationEventItem = ({
  event,
  eventIdx,
  fieldName,
  name,
  events,
  passportEvents,
  formRef,
  canEdit,
  isKnm,
  isPm,
  isDecision,
  id,
}: IErknmActViolationEventItemProps) => {
  const eventsFieldName = `${name}.events`;
  const remove = formRef?.current.mutators.remove;

  const eventOptions = useMemo(() => {
    const allOptions = createPassportEventSelectOptions(passportEvents);
    const usedGuids = new Set(
      (events || [])
        .map((item, idx) => (idx === eventIdx ? null : item.eventOption?.value))
        .filter(Boolean) as string[],
    );

    return allOptions.filter(option => !usedGuids.has(option.value) || option.value === event.eventOption?.value);
  }, [passportEvents, events, eventIdx, event.eventOption?.value]);

  const label = useMemo(() => {
    const base = 'Осуществленное действие';

    return events && events.length > 1 ? `${base} №${eventIdx + 1}` : `${base} №1`;
  }, [events, eventIdx]);

  const handleRemove = useCallback(() => {
    if ((events || []).length <= 1) {
      // @ts-ignore
      formRef?.current.change(`${name}.inEvent` as keyof IKnmForm, negativeSelectOption);
      // @ts-ignore
      formRef?.current.change(eventsFieldName as keyof IKnmForm, undefined);

      return;
    }

    remove && remove(eventsFieldName, eventIdx);
  }, [events, formRef, name, eventsFieldName, remove, eventIdx]);

  const handleEventOptionChange = useCallback(
    (value: ISelectOption | undefined) => {
      if (!canEdit || value?.value === event.eventOption?.value) {
        return;
      }

      // @ts-ignore
      formRef?.current.change(`${fieldName}.materials` as keyof IKnmForm);
    },
    [canEdit, event.eventOption?.value, fieldName, formRef],
  );

  return (
    <>
      <Row notSpace notWrap>
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label,
            field: `${fieldName}.eventOption`,
            isPm,
            isKnm,
            isDecision,
          })}
          placementLabel="left"
          color="black"
          className={styles.FieldBlock}
          labelSize="col-3"
          labelFontSize="small"
          type="not-space"
          id={`${id}.eventOption`}
        >
          <FieldBlock>
            <SelectInputField
              name={`${fieldName}.eventOption`}
              options={eventOptions}
              className={canEdit ? fixFactTypeStyles.FullWidthWithButton : undefined}
              isClearable={false}
              placeholder="Выберите действие"
              disabled={!canEdit}
              validate={makeRequired('Поле не может быть пустым')}
              noOptionsMessage="Значение отсутствует"
              onChange={handleEventOptionChange}
            />
          </FieldBlock>
        </FieldBlock>
        {canEdit && <CloseButton className="shared-deleting-row-btn" onClick={handleRemove} />}
      </Row>
      <ErknmActViolationEventMaterials
        name={fieldName}
        formRef={formRef}
        isKnm={isKnm}
        isPm={isPm}
        isDecision={isDecision}
        id={id}
        canEdit={canEdit}
        selectedEventGuid={event.eventOption?.value || null}
      />
    </>
  );
};
